import type { QuoteResponse } from "@defuse-protocol/one-click-sdk-typescript"
import {
  getHLWithdrawQuote,
  getTxStatus,
} from "@src/components/DefuseSDK/features/machines/1cs"
import { fetchConfidentialExecutionStatus } from "@src/components/DefuseSDK/features/machines/privateIntents"
import type { IntentsUserId } from "@src/components/DefuseSDK/types/intentsUserId"
import { parseUnits } from "@src/components/DefuseSDK/utils/parse"
import {
  isQuoteSignatureError,
  QUOTE_SIGNATURE_INVALID_MESSAGE,
  verifyQuoteResult,
} from "@src/components/DefuseSDK/utils/verifyQuoteSignature"
import {
  type JourneyProvenance,
  journeyAnalytics,
} from "@src/features/analytics/journeyAnalytics"
import {
  HL_WITHDRAW_FEE_USDC,
  HL_WITHDRAW_STATUS_MAX_ATTEMPTS,
  HL_WITHDRAW_STATUS_POLL_MS,
} from "@src/features/trade/hyperliquid/constants/config"
import { createHyperliquidExchangeClient } from "@src/features/trade/hyperliquid/services/hyperliquidClient"
import type { TradingSigner } from "@src/features/tradingSigner"
import { useOneClickQuotesStore } from "@src/stores/oneClickQuotesStore"
import { logger } from "@src/utils/logger"
import { assign, fromPromise, setup } from "xstate"

const USDC_DECIMALS = 6
const QUOTE_DEADLINE_MS = 30 * 60 * 1000
const QUOTE_STALE_AFTER_MS = QUOTE_DEADLINE_MS - 5 * 60 * 1000

type Context = {
  amount: string
  tradingSigner: TradingSigner | null
  intentsUserId: IntentsUserId | null
  depositAddress: string | null
  quoteResult: (QuoteResponse & { appFee: [string, bigint][] }) | null
  quotedAt: number | null
  isConfidential: boolean
  withdrawSubmitted: boolean
  status: string | null
  error: string | null
  analytics: PerpsWithdrawAnalytics | null
}

type PerpsWithdrawAnalytics = Readonly<{
  journey: JourneyProvenance<"perps_withdraw">
  facts: Readonly<{
    asset_symbol: "USDC"
    mode: "public" | "confidential"
    net_value_usd: number
    fee_usd: number
  }>
}>

type Input = {
  amount: string
  tradingSigner: TradingSigner | null
  intentsUserId: IntentsUserId | null
}

type Events =
  | { type: "CONFIRM"; isConfidential?: boolean }
  | { type: "RETRY" }
  | { type: "RESET" }
  | { type: "UPDATE_AMOUNT"; amount: string }
  | { type: "UPDATE_TRADING_SIGNER"; tradingSigner: TradingSigner | null }
  | { type: "UPDATE_INTENTS_USER_ID"; intentsUserId: IntentsUserId | null }

function createPerpsWithdrawAnalytics(
  context: Context
): PerpsWithdrawAnalytics | null {
  const grossValueUsd = Number(context.amount)
  try {
    parseUnits(context.amount, USDC_DECIMALS)
  } catch {
    return null
  }
  if (
    !Number.isFinite(grossValueUsd) ||
    grossValueUsd <= 0 ||
    context.tradingSigner == null ||
    context.intentsUserId == null
  ) {
    return null
  }

  return {
    journey: {
      journeyId: journeyAnalytics.newId(),
      flow: "perps_withdraw",
      privacy: "public",
      valuation: {
        valueUsd: grossValueUsd,
        valueQuality: "denominated",
      },
    },
    facts: {
      asset_symbol: "USDC",
      mode: context.isConfidential ? "confidential" : "public",
      net_value_usd: Math.max(0, grossValueUsd - HL_WITHDRAW_FEE_USDC),
      fee_usd: HL_WITHDRAW_FEE_USDC,
    },
  }
}

const getQuoteActor = fromPromise(
  async ({
    input,
  }: {
    input: {
      amount: string
      tradingSigner: TradingSigner | null
      intentsUserId: IntentsUserId | null
      isConfidential: boolean
    }
  }) => {
    if (input.tradingSigner == null) {
      throw new Error("Trading signer not ready")
    }
    if (input.intentsUserId == null) {
      throw new Error("Intents account not ready")
    }

    // HL deducts the withdraw fee on its side, so only (amount - fee) lands at
    // the deposit address. Quote the net amount in USDC base units.
    const grossUnits = parseUnits(input.amount, USDC_DECIMALS)
    const feeUnits = parseUnits(String(HL_WITHDRAW_FEE_USDC), USDC_DECIMALS)
    const netUnits = grossUnits > feeUnits ? grossUnits - feeUnits : 0n

    const deadline = new Date(Date.now() + QUOTE_DEADLINE_MS).toISOString()
    const result = verifyQuoteResult(
      await getHLWithdrawQuote({
        amount: netUnits.toString(),
        intentsRecipient: input.intentsUserId,
        refundAddress: input.tradingSigner.address,
        deadline,
        ...(input.isConfidential ? { isConfidential: true } : {}),
      })
    )

    if ("err" in result) {
      throw new Error(
        isQuoteSignatureError(result.err)
          ? QUOTE_SIGNATURE_INVALID_MESSAGE
          : result.err
      )
    }

    const depositAddress = result.ok.quote?.depositAddress
    if (!depositAddress) {
      throw new Error("No deposit address returned from 1-Click")
    }

    return { depositAddress, quoteResult: result.ok }
  }
)

// `withdraw3` is the current versioned HL withdraw endpoint name.
const hlWithdrawV3Actor = fromPromise(
  async ({
    input,
  }: {
    input: {
      tradingSigner: TradingSigner
      destination: string
      amount: string
    }
  }) => {
    const exchange = createHyperliquidExchangeClient(input.tradingSigner)
    await exchange.withdraw3({
      destination: input.destination,
      amount: input.amount,
    })
  }
)

const pollStatusActor = fromPromise(
  async ({
    input,
    signal,
  }: {
    input: { depositAddress: string; isConfidential: boolean }
    signal: AbortSignal
  }) => {
    const sleep = (ms: number) =>
      new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, ms)
        signal.addEventListener(
          "abort",
          () => {
            clearTimeout(timer)
            reject(new Error("Aborted"))
          },
          { once: true }
        )
      })

    for (let i = 0; i < HL_WITHDRAW_STATUS_MAX_ATTEMPTS; i++) {
      if (signal.aborted) throw new Error("Aborted")
      const result = input.isConfidential
        ? await fetchConfidentialExecutionStatus(input.depositAddress)
        : await getTxStatus(input.depositAddress)
      if (signal.aborted) throw new Error("Aborted")

      if ("err" in result) {
        logger.error("HL withdraw poll error", { error: result.err })
        await sleep(HL_WITHDRAW_STATUS_POLL_MS)
        continue
      }

      const status = result.ok.status
      if (status === "SUCCESS") {
        return { status }
      }
      if (
        status === "FAILED" ||
        (input.isConfidential && status === "FAILED_EXECUTION") ||
        status === "REFUNDED"
      ) {
        throw new Error(`Deposit ${status.toLowerCase()}`)
      }

      await sleep(HL_WITHDRAW_STATUS_POLL_MS)
    }
    return { status: "PENDING" }
  }
)

function shouldClearCachedQuote(
  context: Context,
  nextIntentsUserId: IntentsUserId | null
) {
  return (
    !context.withdrawSubmitted && context.intentsUserId !== nextIntentsUserId
  )
}

export const hlWithdrawToIntentsMachine = setup({
  types: {
    context: {} as Context,
    input: {} as Input,
    events: {} as Events,
  },
  actors: {
    getQuote: getQuoteActor,
    withdraw3: hlWithdrawV3Actor,
    pollStatus: pollStatusActor,
  },
  actions: {
    clearProgress: assign({
      depositAddress: () => null,
      quoteResult: () => null,
      quotedAt: () => null,
      withdrawSubmitted: () => false,
      status: () => null,
      error: () => null,
    }),
    clearAnalytics: assign({ analytics: () => null }),
    setAnalyticsJourney: assign({
      analytics: ({ context }) => createPerpsWithdrawAnalytics(context),
    }),
    emitAnalyticsJourneyStarted: ({ context }) => {
      if (context.analytics == null) return
      journeyAnalytics.started({
        ...context.analytics.facts,
        journey: context.analytics.journey,
        surface: "perps",
      })
    },
    emitAnalyticsSubmitted: ({ context }) => {
      if (context.analytics == null || context.depositAddress == null) return
      journeyAnalytics.submitted({
        ...context.analytics.facts,
        journey: context.analytics.journey,
        surface: "perps",
        operation: { namespace: "one_click", key: context.depositAddress },
      })
    },
    emitAnalyticsCompleted: ({ context }) => {
      if (context.analytics == null || context.depositAddress == null) return
      journeyAnalytics.completed({
        ...context.analytics.facts,
        journey: context.analytics.journey,
        surface: "perps",
        operation: { namespace: "one_click", key: context.depositAddress },
      })
    },
    saveOneClickQuote: ({ context }) => {
      if (context.quoteResult == null) return
      useOneClickQuotesStore.getState().saveQuote(context.quoteResult)
    },
  },
}).createMachine({
  id: "hlWithdrawToIntents",
  initial: "idle",
  context: ({ input }) => ({
    amount: input.amount,
    tradingSigner: input.tradingSigner,
    intentsUserId: input.intentsUserId,
    isConfidential: false,
    depositAddress: null,
    quoteResult: null,
    quotedAt: null,
    withdrawSubmitted: false,
    status: null,
    error: null,
    analytics: null,
  }),
  states: {
    idle: {
      on: {
        CONFIRM: {
          target: "gettingQuote",
          actions: [
            assign({
              isConfidential: ({ event }) => event.isConfidential ?? false,
            }),
            "setAnalyticsJourney",
            "emitAnalyticsJourneyStarted",
          ],
        },
        UPDATE_AMOUNT: {
          actions: assign({
            amount: ({ event }) => event.amount,
          }),
        },
        UPDATE_TRADING_SIGNER: {
          actions: assign({
            tradingSigner: ({ event }) => event.tradingSigner,
          }),
        },
        UPDATE_INTENTS_USER_ID: {
          actions: assign({
            intentsUserId: ({ event }) => event.intentsUserId,
            depositAddress: ({ context, event }) =>
              shouldClearCachedQuote(context, event.intentsUserId)
                ? null
                : context.depositAddress,
            quoteResult: ({ context, event }) =>
              shouldClearCachedQuote(context, event.intentsUserId)
                ? null
                : context.quoteResult,
            quotedAt: ({ context, event }) =>
              shouldClearCachedQuote(context, event.intentsUserId)
                ? null
                : context.quotedAt,
          }),
        },
      },
    },

    gettingQuote: {
      invoke: {
        src: "getQuote",
        input: ({ context }) => ({
          amount: context.amount,
          tradingSigner: context.tradingSigner,
          intentsUserId: context.intentsUserId,
          isConfidential: context.isConfidential,
        }),
        onDone: {
          target: "signingWithdraw",
          actions: assign({
            depositAddress: ({ event }) => event.output.depositAddress,
            quoteResult: ({ event }) => event.output.quoteResult,
            quotedAt: () => Date.now(),
            withdrawSubmitted: () => false,
          }),
        },
        onError: {
          target: "error",
          actions: assign({
            error: ({ event }) =>
              event.error instanceof Error
                ? event.error.message
                : "Failed to get quote",
          }),
        },
      },
    },

    signingWithdraw: {
      invoke: {
        src: "withdraw3",
        input: ({ context }) => {
          if (!context.tradingSigner) {
            throw new Error("Trading signer not ready")
          }
          if (!context.depositAddress) {
            throw new Error("Deposit address not available")
          }
          return {
            tradingSigner: context.tradingSigner,
            destination: context.depositAddress,
            amount: context.amount,
          }
        },
        onDone: {
          target: "polling",
          actions: [
            assign({
              withdrawSubmitted: () => true,
            }),
            "saveOneClickQuote",
            "emitAnalyticsSubmitted",
          ],
        },
        onError: {
          target: "error",
          actions: assign({
            withdrawSubmitted: ({ event }) =>
              isAmbiguousWithdrawSubmissionError(event.error),
            error: ({ event }) =>
              event.error instanceof Error
                ? event.error.message
                : "Withdrawal signing failed",
          }),
        },
      },
    },

    polling: {
      invoke: {
        src: "pollStatus",
        input: ({ context }) => ({
          depositAddress: context.depositAddress ?? "",
          isConfidential: context.isConfidential,
        }),
        onDone: [
          {
            guard: ({ event }) => event.output.status === "SUCCESS",
            target: "completed",
            actions: [
              assign({
                status: ({ event }) => event.output.status,
              }),
              "emitAnalyticsCompleted",
            ],
          },
          {
            target: "processing",
            actions: assign({
              status: ({ event }) => event.output.status,
              error: () => null,
            }),
          },
        ],
        onError: {
          target: "error",
          actions: assign({
            error: ({ event }) =>
              event.error instanceof Error
                ? event.error.message
                : "Deposit failed",
          }),
        },
      },
    },

    processing: {
      after: {
        30000: "polling",
      },
      on: {
        RETRY: "polling",
        UPDATE_TRADING_SIGNER: {
          actions: assign({
            tradingSigner: ({ event }) => event.tradingSigner,
          }),
        },
        UPDATE_INTENTS_USER_ID: {
          actions: assign({
            intentsUserId: ({ event }) => event.intentsUserId,
            depositAddress: ({ context, event }) =>
              shouldClearCachedQuote(context, event.intentsUserId)
                ? null
                : context.depositAddress,
            quoteResult: ({ context, event }) =>
              shouldClearCachedQuote(context, event.intentsUserId)
                ? null
                : context.quoteResult,
            quotedAt: ({ context, event }) =>
              shouldClearCachedQuote(context, event.intentsUserId)
                ? null
                : context.quotedAt,
          }),
        },
        RESET: {
          target: "idle",
          actions: ["clearProgress", "clearAnalytics"],
        },
      },
    },

    completed: {
      on: {
        RESET: {
          target: "idle",
          actions: ["clearProgress", "clearAnalytics"],
        },
      },
    },

    error: {
      on: {
        UPDATE_TRADING_SIGNER: {
          actions: assign({
            tradingSigner: ({ event }) => event.tradingSigner,
          }),
        },
        UPDATE_INTENTS_USER_ID: {
          actions: assign({
            intentsUserId: ({ event }) => event.intentsUserId,
            depositAddress: ({ context, event }) =>
              shouldClearCachedQuote(context, event.intentsUserId)
                ? null
                : context.depositAddress,
            quoteResult: ({ context, event }) =>
              shouldClearCachedQuote(context, event.intentsUserId)
                ? null
                : context.quoteResult,
            quotedAt: ({ context, event }) =>
              shouldClearCachedQuote(context, event.intentsUserId)
                ? null
                : context.quotedAt,
          }),
        },
        RETRY: [
          {
            guard: ({ context }) =>
              context.withdrawSubmitted && context.depositAddress != null,
            target: "polling",
          },
          {
            guard: ({ context }) =>
              !context.withdrawSubmitted &&
              context.quotedAt != null &&
              Date.now() - context.quotedAt >= QUOTE_STALE_AFTER_MS,
            target: "gettingQuote",
            actions: [
              "clearProgress",
              "setAnalyticsJourney",
              "emitAnalyticsJourneyStarted",
            ],
          },
          {
            guard: ({ context }) => context.depositAddress != null,
            target: "signingWithdraw",
            actions: ["setAnalyticsJourney", "emitAnalyticsJourneyStarted"],
          },
          {
            target: "gettingQuote",
            actions: ["setAnalyticsJourney", "emitAnalyticsJourneyStarted"],
          },
        ],
        RESET: {
          target: "idle",
          actions: ["clearProgress", "clearAnalytics"],
        },
      },
    },
  },
})

function isAmbiguousWithdrawSubmissionError(error: unknown): boolean {
  if (isUserRejectedWithdraw(error)) {
    return false
  }
  return true
}

function isUserRejectedWithdraw(error: unknown): boolean {
  const code = getErrorCode(error)
  if (code === 4001 || code === "ACTION_REJECTED") {
    return true
  }

  const message = getErrorMessage(error).toLowerCase()
  return (
    message.includes("user rejected") ||
    message.includes("user denied") ||
    message.includes("rejected signature") ||
    message.includes("signature rejected") ||
    message.includes("request rejected") ||
    message.includes("request denied")
  )
}

function getErrorCode(error: unknown): unknown {
  return typeof error === "object" && error !== null && "code" in error
    ? (error as { code?: unknown }).code
    : null
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}
